// Shared offline-first photo queue hook — the pipeline originally built for
// Audits & Inspections (RichFormInstance), generalised for any record type
// registered in lib/photoUploadTargets.js: compress on-device -> write-ahead to
// IndexedDB with a client_upload_id (safe on disk before any network call) ->
// upload via the shared flusher with exponential backoff. A photo is never lost
// to a dropped connection, a closed tab, or a tab-close/crash mid-flight.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { queuePhoto, getQueuedPhotos, requestPersistentStorage } from "@/lib/offlineDraft";
import {
    compressPhoto, newClientUploadId, flushDraftKey, retryOneQueuedPhoto,
    discardOneQueuedPhoto, discardAllQueuedPhotos,
} from "@/lib/photoFlusher";

export function useOfflinePhotoQueue({ entityType, entityId, draftKey: draftKeyOverride, recordLabel, enabled = true, onUploaded } = {}) {
    const draftKey = draftKeyOverride || (entityType && entityId ? `photo:${entityType}:${entityId}` : null);
    const [pendingPhotos, setPendingPhotos] = useState([]);
    const onUploadedRef = useRef(onUploaded);
    onUploadedRef.current = onUploaded;

    const refresh = useCallback(async () => {
        if (!draftKey) return;
        const queued = await getQueuedPhotos(draftKey);
        setPendingPhotos(queued.map((q) => ({ tempId: q.id, ...q.meta, previewUrl: URL.createObjectURL(q.blob) })));
    }, [draftKey]);

    useEffect(() => { if (enabled && draftKey) refresh(); }, [enabled, draftKey, refresh]);

    // React to uploads/failures happening elsewhere for THIS record — e.g. the
    // app-level background flusher succeeding while this page happens to be open.
    useEffect(() => {
        if (!draftKey) return;
        const onChange = (e) => {
            if (e.detail?.draftKey !== draftKey) return;
            refresh();
            if (e.detail.type === "uploaded" && onUploadedRef.current) onUploadedRef.current(e.detail.payload);
        };
        window.addEventListener("pr-photo-queue-changed", onChange);
        return () => window.removeEventListener("pr-photo-queue-changed", onChange);
    }, [draftKey, refresh]);

    const flush = useCallback(async () => { if (draftKey) await flushDraftKey(draftKey); }, [draftKey]);

    // Proactively retry on load/foreground/interval — relying solely on the browser
    // "online" event misses the common case of a page opened while already online
    // after queuing happened during a spotty-signal site visit.
    useEffect(() => {
        if (!enabled || !draftKey) return;
        requestPersistentStorage();
        if (navigator.onLine) flush();
        const onVisible = () => { if (document.visibilityState === "visible" && navigator.onLine) flush(); };
        document.addEventListener("visibilitychange", onVisible);
        const onOnline = () => flush();
        window.addEventListener("online", onOnline);
        const interval = setInterval(() => { if (navigator.onLine) flush(); }, 20000);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("online", onOnline);
            clearInterval(interval);
        };
    }, [enabled, draftKey, flush]);

    const queuePhotoFile = useCallback(async (file, meta = {}) => {
        if (!draftKey) return;
        const clientUploadId = newClientUploadId();
        const { blob, filename } = await compressPhoto(file, { preserveOriginal: !!meta.preserve_original });
        await queuePhoto(draftKey, { ...meta, filename, client_upload_id: clientUploadId, entityType, entityId, recordLabel }, blob);
        await refresh();
        if (navigator.onLine) flush();
    }, [draftKey, entityType, entityId, recordLabel, refresh, flush]);

    const retry = useCallback(async (tempId) => {
        setPendingPhotos((p) => p.map((pp) => (pp.tempId === tempId ? { ...pp, retrying: true } : pp)));
        const res = await retryOneQueuedPhoto(draftKey, tempId);
        await refresh();
        if (res.ok) toast.success("Photo uploaded"); else toast.error(`Retry failed: ${res.error || "Still offline"}`);
    }, [draftKey, refresh]);

    const discard = useCallback(async (tempId) => {
        await discardOneQueuedPhoto(draftKey, tempId);
        await refresh();
        toast.success("Photo discarded");
    }, [draftKey, refresh]);

    const discardAll = useCallback(async () => {
        await discardAllQueuedPhotos(draftKey);
        await refresh();
        toast.success("Pending photos discarded");
    }, [draftKey, refresh]);

    return { pendingPhotos, queuePhoto: queuePhotoFile, flush, retry, discard, discardAll };
}
